// Phase 7 — Site Settings schema.
// Controlled allow-list of editable global settings. The admin UI only
// exposes fields defined here; arbitrary settings cannot be created from UI.

export type SettingType = "text" | "textarea" | "url" | "phone" | "email" | "image_url";

export interface SettingFieldDef {
  setting_key: string;
  label: string;
  type: SettingType;
  helper?: string;
  placeholder?: string;
}

export interface SettingGroupDef {
  group_key: string;
  label: string;
  description?: string;
  fields: SettingFieldDef[];
}

export const SITE_SETTINGS_SCHEMA: SettingGroupDef[] = [
  {
    group_key: "contact",
    label: "Business Contact",
    description: "Phone, email, and mailing address shown across the site.",
    fields: [
      { setting_key: "contact.phone", label: "Phone", type: "phone", placeholder: "(781) 632-3936" },
      { setting_key: "contact.email", label: "Email", type: "email", placeholder: "Info@hadassahhomesinc.com" },
      { setting_key: "contact.address_line_1", label: "Address Line 1", type: "text" },
      { setting_key: "contact.address_line_2", label: "Address Line 2", type: "text" },
      { setting_key: "contact.city", label: "City", type: "text" },
      { setting_key: "contact.state", label: "State", type: "text" },
      { setting_key: "contact.zip", label: "ZIP", type: "text" },
      { setting_key: "contact.country", label: "Country", type: "text" },
    ],
  },
  {
    group_key: "social",
    label: "Social Links",
    description: "Public social profile URLs.",
    fields: [
      { setting_key: "social.facebook_url", label: "Facebook URL", type: "url" },
      { setting_key: "social.instagram_url", label: "Instagram URL", type: "url" },
      { setting_key: "social.linkedin_url", label: "LinkedIn URL", type: "url" },
      { setting_key: "social.youtube_url", label: "YouTube URL", type: "url" },
      { setting_key: "social.tiktok_url", label: "TikTok URL", type: "url" },
    ],
  },
  {
    group_key: "footer",
    label: "Footer",
    fields: [
      { setting_key: "footer.description", label: "Footer description", type: "textarea" },
      { setting_key: "footer.copyright_text", label: "Copyright text", type: "text", helper: "Year is added automatically if blank." },
      { setting_key: "footer.primary_cta_label", label: "Primary CTA label", type: "text" },
      { setting_key: "footer.primary_cta_url", label: "Primary CTA URL", type: "url" },
    ],
  },
  {
    group_key: "global_cta",
    label: "Global CTA",
    fields: [
      { setting_key: "global_cta.consultation_label", label: "Consultation CTA label", type: "text" },
      { setting_key: "global_cta.consultation_url", label: "Consultation CTA URL", type: "url" },
      { setting_key: "global_cta.properties_label", label: "Properties CTA label", type: "text" },
      { setting_key: "global_cta.properties_url", label: "Properties CTA URL", type: "url" },
    ],
  },
  {
    group_key: "business",
    label: "Business Details",
    fields: [
      { setting_key: "business.name", label: "Business name", type: "text" },
      { setting_key: "business.legal_name", label: "Legal name", type: "text" },
      { setting_key: "business.service_area", label: "Service area", type: "text" },
      { setting_key: "business.hours", label: "Business hours", type: "textarea" },
    ],
  },
];

export const ALL_SETTING_KEYS = SITE_SETTINGS_SCHEMA.flatMap((g) =>
  g.fields.map((f) => f.setting_key),
);

export function findFieldDef(setting_key: string): SettingFieldDef | undefined {
  for (const g of SITE_SETTINGS_SCHEMA) {
    const f = g.fields.find((x) => x.setting_key === setting_key);
    if (f) return f;
  }
  return undefined;
}

export function isValidUrlOrPath(v: string): boolean {
  const t = v.trim();
  if (!t) return true;
  if (t.startsWith("/") || t.startsWith("#")) return true;
  try {
    new URL(t);
    return true;
  } catch {
    return false;
  }
}

export function isValidEmail(v: string): boolean {
  const t = v.trim();
  if (!t) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

export function isValidPhone(v: string): boolean {
  const t = v.trim();
  if (!t) return true;
  // Light validation: at least 7 digits, allow common phone chars
  const digits = t.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 20) return false;
  return /^[+()\-.\s\d]+$/.test(t);
}

export function validateSetting(type: SettingType, value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  switch (type) {
    case "url":
    case "image_url":
      return isValidUrlOrPath(v) ? null : "Must be /path, #anchor, or a full URL";
    case "email":
      return isValidEmail(v) ? null : "Invalid email address";
    case "phone":
      return isValidPhone(v) ? null : "Invalid phone number";
    default:
      return null;
  }
}
